/**
 * Ritualists Tab
 * Displays community members with RITUALIST role with filtering (All/Highlighted)
 */

import { EllipsisVertical, Loader2, Search, Star, User, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { GuestAction } from "@/components/guest"
import { HeartFilledIcon, HeartOutlineIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useCommunityRitualistsInfinite } from "../hooks/useCommunityRitualistsInfinite"
import { useRemoveRitualist } from "../hooks/useRemoveRitualist"
import { useToggleSaveRitualist } from "../hooks/useToggleSaveRitualist"
import type { CommunityMember } from "../services/community.service"
import type { CommunityDetail } from "../types/community-detail"
import { InviteModal } from "./InviteModal"

type SubTab = "all" | "highlighted"

interface RitualistsTabProps {
  communityId: string
  searchQuery?: string
  permissions?: CommunityDetail["permissions"]
  currentUserId?: string
  creatorId?: string
  showSearch?: boolean
  onToggleSearch?: () => void
  onSearchChange?: (value: string) => void
  isGuest?: boolean
}

export function RitualistsTab({
  communityId,
  searchQuery = "",
  permissions,
  currentUserId,
  creatorId,
  showSearch,
  onToggleSearch,
  onSearchChange,
  isGuest = false,
}: RitualistsTabProps) {
  const { t } = useTranslation("communities")
  const navigate = useNavigate()

  // Debounce search query to avoid API calls on every keystroke
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Sub-tab state for filtering
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("all")

  // Hovered ritualist state
  const [hoveredRitualist, setHoveredRitualist] = useState<CommunityMember | null>(null)

  // Local state to track follow status changes immediately for hovered ritualist
  const [hoveredFollowStatus, setHoveredFollowStatus] = useState<Record<string, boolean>>({})

  // Menu open state for hovered ritualist
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Invite modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Fetch ritualists based on highlighted filter and search query
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useCommunityRitualistsInfinite({
      communityId,
      highlighted: activeSubTab === "highlighted",
      searchQuery: debouncedSearch || undefined,
    })

  // Toggle save ritualist mutation
  const { mutate: toggleSave, isPending: isTogglingSave } = useToggleSaveRitualist({ communityId })

  // Follow/unfollow mutation
  const { follow, unfollow, isFollowing, isUnfollowing } = useFollowActions()

  // Remove ritualist mutation
  const { mutate: removeRitualist, isPending: isRemoving } = useRemoveRitualist({ communityId })

  // Get all ritualists from all pages
  const allRitualists = data?.pages.flatMap(page => page.members) ?? []
  const totalCount = data?.pages?.[0]?.meta?.AllRitualists ?? allRitualists.length

  // Server-side search is now used via API - no local filtering needed
  const filteredRitualists = allRitualists

  // Fetch highlighted count only when on "all" tab (to avoid query key collision)
  const { data: highlightedData } = useCommunityRitualistsInfinite({
    communityId,
    highlighted: true,
    searchQuery: debouncedSearch || undefined,
  })
  const highlightedCount = highlightedData?.pages?.[0]?.meta?.highlightedRitualists ?? 0

  // Reset to first page when sub-tab changes
  useEffect(() => {
    refetch()
  }, [refetch])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedAnyMenu = Array.from(menuRefs.current.values()).some(el =>
        el?.contains(event.target as Node),
      )
      if (!clickedAnyMenu) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation()
    navigate(`/profile/${userId}`)
  }

  const handleFollowToggle = (e: React.MouseEvent, ritualist: CommunityMember) => {
    e.stopPropagation()
    // Get current follow status - use local state if updated, otherwise use data from API
    const currentStatus =
      hoveredFollowStatus[ritualist.id] !== undefined
        ? hoveredFollowStatus[ritualist.id]
        : ritualist.isFollowing

    // Update local state immediately for optimistic UI
    setHoveredFollowStatus(prev => ({
      ...prev,
      [ritualist.id]: !currentStatus,
    }))
    if (currentStatus) {
      unfollow(ritualist.id)
    } else {
      follow(ritualist.id)
    }
  }

  const handleToggleSave = (e: React.MouseEvent, ritualistId: string) => {
    e.stopPropagation()
    toggleSave(ritualistId)
  }

  const handleRemoveRitualist = (e: React.MouseEvent, ritualistId: string) => {
    e.stopPropagation()
    removeRitualist(ritualistId)
  }

  const handleMenuClick = (e: React.MouseEvent, ritualistId: string) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === ritualistId ? null : ritualistId)
  }

  const handleMouseEnter = (ritualist: CommunityMember) => {
    setHoveredRitualist(ritualist)
  }

  const handleMouseLeave = () => {
    setHoveredRitualist(null)
    setOpenMenuId(null)
  }

  return (
    <div className="md:py-10 md:pb-15">
      {/* Header with sub-tabs and admin button */}
      <div className="flex items-center justify-between md:justify-start gap-4 mb-2.5 md:mb-6 h-10">
        <h2 className="text-lg font-semibold text-primary leading-[30px] uppercase">
          {t("tabs.ritualists")}
        </h2>

        <div className="flex flex-col md:flex-row md:flex-1 items-center justify-between">
          {/* Sub-tabs: All and Highlighted */}
          <div className="hidden md:flex items-center md:border-l ms-1 gap-5 mb-5 md:mb-0">
            <button
              type="button"
              onClick={() => setActiveSubTab("all")}
              className={`pl-4 py-1 text-sm cursor-pointer font-medium transition-colors ${
                activeSubTab === "all" ? "text-primary" : "text-body"
              }`}
            >
              {"All"} ({totalCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveSubTab("highlighted")}
              className={`pr-4 py-1 text-sm cursor-pointer font-medium transition-colors ${
                activeSubTab === "highlighted" ? "text-primary" : "text-body"
              }`}
            >
              {"Highlighted"} ({highlightedCount})
            </button>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {/* Add ritualist button - only for admins */}
            {permissions?.canManageMembers && (
              <Button
                variant="primary"
                size="md"
                className="flex items-center gap-2 px-15 py-3 uppercase"
                onClick={() => setIsInviteModalOpen(true)}
              >
                {"Add Ritualist"}
              </Button>
            )}
          </div>
          {/* Mobile search toggle */}
          <div className="md:hidden flex items-center h-full justify-end w-full">
            {showSearch ? (
              <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5">
                <Search className="w-4 h-4 text-ash shrink-0" />
                <Input
                  type="text"
                  placeholder={t("searchPlaceholder")}
                  value={searchQuery}
                  onChange={e => onSearchChange?.(e.target.value)}
                  className="max-w-40 h-5 border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-6 w-6 text-ash"
                  onClick={onToggleSearch}
                  aria-label="Close search"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className="opacity-50 cursor-pointer"
                onClick={() => (isGuest ? setShowLoginModal(true) : onToggleSearch?.())}
                aria-label="Search"
              >
                <Search className="w-5 h-5 text-charcoal" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="md:hidden flex items-center md:border-l gap-5 mb-7.5 md:mb-0">
        <button
          type="button"
          onClick={() => setActiveSubTab("all")}
          className={`py-1 text-sm cursor-pointer font-semibold transition-colors ${
            activeSubTab === "all" ? "text-primary" : "text-body"
          }`}
        >
          {"All"} ({totalCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab("highlighted")}
          className={`pr-4 py-1 text-sm cursor-pointer font-semibold transition-colors ${
            activeSubTab === "highlighted" ? "text-primary" : "text-body"
          }`}
        >
          {"Highlighted"} ({highlightedCount})
        </button>
      </div>

      {/* Invite Modal */}
      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        communityId={communityId}
        roleType="ritualist"
        onSuccess={() => refetch()}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error State */}
      {isLoading === false && (
        <div className="mt-7">
          {filteredRitualists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <User className="mb-2 h-12 w-12 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? t("tabs.noRitualistsFound")
                  : activeSubTab === "highlighted"
                    ? t("tabs.noHighlightedRitualists")
                    : t("tabs.noRitualists")}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-9">
                {filteredRitualists.map(ritualist => (
                  <div
                    key={ritualist.id}
                    className="relative flex flex-col items-center rounded-2xl border border-card bg-card p-4 transition-colors hover:bg-muted/50 cursor-pointer overflow-hidden"
                    onMouseEnter={() => handleMouseEnter(ritualist)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Star button - top left */}
                    <button
                      type="button"
                      className="absolute top-2.5 left-2.5 z-10 p-1 rounded-full hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer overflow-hidden"
                      onClick={e => {
                        e.stopPropagation()
                        handleToggleSave(e, ritualist.id)
                      }}
                      disabled={isTogglingSave}
                    >
                      {!!ritualist.isSaved && <Star className="h-6 w-6 fill-white text-white" />}
                    </button>

                    {/* Avatar - full absolute with object-cover */}
                    <div
                      className="w-full aspect-square overflow-hidden rounded-lg p-1 cursor-pointer"
                      onClick={e => handleProfileClick(e, ritualist.id)}
                    >
                      {ritualist.coverImage ? (
                        <img
                          src={ritualist.coverImage}
                          alt={ritualist.name || "User"}
                          className="absolute aspect-[300/300] inset-0 object-cover overflow-hidden"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-themed overflow-hidden">
                          <User className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-[rgba(0,0,0,0.1)]" />
                    </div>

                    {/* User Info */}
                    <div
                      className="absolute flex px-4 py-3 text-left items-end justify-between bottom-0 w-full bg-white/10 backdrop-blur-md"
                      style={{
                        WebkitMaskImage: "linear-gradient(to top, black 70%, transparent 100%)",
                        maskImage: "linear-gradient(to top, black 70%, transparent 100%)",
                      }}
                    >
                      <div>
                        <p
                          className="truncate text-[17px] font-monteserat font-semibold cursor-pointer"
                          onClick={e => handleProfileClick(e, ritualist.id)}
                        >
                          {ritualist.name}
                        </p>
                        <p
                          className="truncate text-[13px] text-muted-foreground cursor-pointer"
                          onClick={e => handleProfileClick(e, ritualist.id)}
                        >
                          {ritualist.username ? `@${ritualist.username}` : "No username"}
                        </p>
                      </div>
                      <p className="text-[13px] font-monteserat leading-[20px]">
                        {ritualist.ritualsCreatedCount !== undefined && (
                          <span className="ml-1">
                            {ritualist.ritualsCreatedCount}{" "}
                            {ritualist.ritualsCreatedCount === 1 ? "ritual" : "rituals"}
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Inline Overlay - appears on hover */}
                    {hoveredRitualist?.id === ritualist.id && (
                      <div className="absolute inset-0 z-20 flex flex-col rounded-lg p-4 overflow-hidden">
                        {/* Image in overlay */}
                        <div className="absolute inset-0">
                          {ritualist.coverImage ? (
                            <img
                              src={ritualist.coverImage}
                              alt={ritualist.name || "User"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-themed">
                              <User className="h-16 w-16 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(2,2,2,0.3),rgba(2,2,2,0.7)),linear-gradient(to_top,rgba(2,2,2,0.3),rgba(2,2,2,0.3))] backdrop-blur-sm" />
                        </div>

                        {/* Menu Button - top right - hidden for own card */}
                        {ritualist.id !== currentUserId && permissions?.canManageMembers && (
                          <div
                            className="absolute top-3 right-3 z-30"
                            ref={el => {
                              if (el) menuRefs.current.set(ritualist.id, el)
                              else menuRefs.current.delete(ritualist.id)
                            }}
                          >
                            <button
                              type="button"
                              className="p-1.5 cursor-pointer z-30 relative"
                              onClick={e => handleMenuClick(e, ritualist.id)}
                            >
                              <EllipsisVertical className="h-5 w-5 text-white" />
                            </button>

                            {/* Dropdown Menu */}
                            {openMenuId === ritualist.id && (
                              <div className="absolute right-0 p-2 top-8 w-50 bg-popover text-popover-foreground rounded-lg shadow-lg border border-border overflow-visible z-50 space-y-1">
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-1 text-[14px] text-left hover:bg-muted/50 transition-colors cursor-pointer"
                                  onClick={e => {
                                    handleToggleSave(e, ritualist.id)
                                    setOpenMenuId(null)
                                  }}
                                  disabled={isTogglingSave}
                                >
                                  <Star className={`h-5 w-5 fill-white`} />
                                  {ritualist.isSaved ? "Remove Star" : "Star Ritualist"}
                                </button>

                                {permissions?.canManageMembers &&
                                  creatorId &&
                                  ritualist.id !== creatorId && (
                                    <button
                                      type="button"
                                      className="w-full flex items-center gap-2 px-3 py-1 text-[14px] text-left text-destructive hover:bg-muted/50 transition-colors cursor-pointer text-brand-terracotta"
                                      onClick={e => {
                                        handleRemoveRitualist(e, ritualist.id)
                                      }}
                                      disabled={isRemoving}
                                    >
                                      <HeartFilledIcon width={21} height={21} color="#C86757" />
                                      Remove Ritualist
                                    </button>
                                  )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Close button - top left */}

                        {/* Content - centered */}
                        <div className="flex-1 flex flex-col items-start justify-end mt-2 h-full p-1 z-20">
                          {/* Name and Username */}
                          <div className="text-center mb-4">
                            <p className="text-[17px] font-semibold text-white text-left">
                              {ritualist.name || "Unnamed User"}
                            </p>
                            <p className="text-[13px] text-white/70 text-left">
                              {ritualist.username ? `@${ritualist.username}` : "No username"}
                            </p>
                          </div>

                          {/* Description - show bio or description if available */}
                          {(ritualist.bio || ritualist.description) && (
                            <p className="text-[14px] text-white/60 leading-[25px] line-clamp-3 mb-4">
                              {ritualist.bio || ritualist.description}
                            </p>
                          )}

                          {/* Action Buttons - hidden for own card */}
                          {ritualist.id !== currentUserId && (
                            <div className="flex gap-2 w-full">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-xs py-4 px-2 uppercase h-auto max-h-[50px]"
                                onClick={e => handleProfileClick(e, ritualist.id)}
                              >
                                Profile
                              </Button>

                              {isGuest ? (
                                <GuestAction>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="flex-1 text-xs py-4 px-2 uppercase h-auto max-h-[50px]"
                                  >
                                    <HeartOutlineIcon width={18} height={18} color="#020202" />
                                    Follow
                                  </Button>
                                </GuestAction>
                              ) : (
                                <Button
                                  variant={
                                    (
                                      hoveredFollowStatus[ritualist.id] !== undefined
                                        ? hoveredFollowStatus[ritualist.id]
                                        : ritualist.isFollowing
                                    )
                                      ? "secondary"
                                      : "primary"
                                  }
                                  size="sm"
                                  className="flex-1 text-xs py-4 px-2 uppercase h-auto max-h-[50px]"
                                  onClick={e => handleFollowToggle(e, ritualist)}
                                  disabled={
                                    isFollowing || isUnfollowing || ritualist.id === currentUserId
                                  }
                                >
                                  {(
                                    hoveredFollowStatus[ritualist.id] !== undefined
                                      ? hoveredFollowStatus[ritualist.id]
                                      : ritualist.isFollowing
                                  ) ? (
                                    <HeartFilledIcon width={18} height={18} color="#020202" />
                                  ) : (
                                    <HeartOutlineIcon width={18} height={18} color="#020202" />
                                  )}
                                  {(
                                    hoveredFollowStatus[ritualist.id] !== undefined
                                      ? hoveredFollowStatus[ritualist.id]
                                      : ritualist.isFollowing
                                  )
                                    ? "Unfollow"
                                    : "Follow"}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {!searchQuery && hasNextPage && (
                <div className="py-4 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
